"use strict";
import { ITEMTYPE, SOCKETABLE_ITEM_TYPES } from "../../js/gamedata/inventorydata";
import { buildCategoryCatalog, filterCatalog } from "../../js/gamedata/gmitemcatalog";
import { buildSkillCategoryCatalog, filterSkillCatalog } from "../../js/gamedata/gmskillcatalog";
import { IconManager } from "../../js/helpers/iconmanager";
import { TooltipManager } from "../../js/helpers/tooltipmanager";

ui.loadTemplateFile("gamemaster.html");

const GM_ITEM_CATEGORIES = [
    { type: ITEMTYPE.FACE_ITEM, listClass: ".items-face" },
    { type: ITEMTYPE.HELMET, listClass: ".items-helmet" },
    { type: ITEMTYPE.ARMOR, listClass: ".items-armor" },
    { type: ITEMTYPE.ARMS, listClass: ".items-arms" },
    { type: ITEMTYPE.BOOTS, listClass: ".items-boots" },
    { type: ITEMTYPE.BACK, listClass: ".items-back" },
    { type: ITEMTYPE.JEWEL, listClass: ".items-jewel" },
    { type: ITEMTYPE.WEAPON, listClass: ".items-weapon" },
    { type: ITEMTYPE.SHIELD, listClass: ".items-shield" },
    { type: ITEMTYPE.USE, listClass: ".items-use" },
    { type: ITEMTYPE.GEM, listClass: ".items-gem" },
    { type: ITEMTYPE.NATURAL, listClass: ".items-natural" },
    { type: ITEMTYPE.QUEST, listClass: ".items-quest" },
    { type: ITEMTYPE.RIDE_PART, listClass: ".items-ridepart" },
];

const GM_SKILL_CATEGORIES = [
    { category: "Basic", listClass: ".skills-basic" },
    { category: "Soldier", listClass: ".skills-soldier" },
    { category: "Muse", listClass: ".skills-muse" },
    { category: "Hawker", listClass: ".skills-hawker" },
    { category: "Dealer", listClass: ".skills-dealer" },
    { category: "Special", listClass: ".skills-special" },
    { category: "GM Skills", listClass: ".skills-gm" },
    { category: "Other", listClass: ".skills-other" },
];

class GamemasterDialog extends Dialog {
    _element: any;
    _titleBar: any;
    _search: any;
    _categories: any[];
    _quantityDialog: any;
    _skillSearch: any;
    _skillCategories: any[];

    constructor() {
        super("gamemaster.html");

        ui.tabpanel(this, ".tabpanel.page");
        ui.tabpanel(this, ".tabpanel.category");
        ui.tabpanel(this, ".tabpanel.skillcategory");

        this._quantityDialog = null;

        this._search = ui.textbox(this, ".textbox.search");
        this._search._element.on("input", this._onSearchChanged.bind(this));

        this._categories = GM_ITEM_CATEGORIES.map((category) => ({
            type: category.type,
            list: ui.list(this, ".list" + category.listClass),
            catalog: buildCategoryCatalog(category.type),
        }));

        for (const category of this._categories) {
            this._renderCategory(category, category.catalog);
        }

        this._skillSearch = ui.textbox(this, ".textbox.skillsearch");
        this._skillSearch._element.on("input", this._onSkillSearchChanged.bind(this));

        this._skillCategories = GM_SKILL_CATEGORIES.map((category) => ({
            category: category.category,
            list: ui.list(this, ".list" + category.listClass),
            catalog: buildSkillCategoryCatalog(category.category),
        }));

        for (const category of this._skillCategories) {
            this._renderSkillCategory(category, category.catalog);
        }

        this.center();
    }

    _onSearchChanged() {
        const searchText = this._search.text();
        for (const category of this._categories) {
            this._renderCategory(category, filterCatalog(category.catalog, searchText));
        }
    }

    _onSkillSearchChanged() {
        const searchText = this._skillSearch.text();
        for (const category of this._skillCategories) {
            this._renderSkillCategory(category, filterSkillCatalog(category.catalog, searchText));
        }
    }

    _renderCategory(category: any, entries: any[]) {
        category.list.clear();

        for (const entry of entries) {
            const item = ui.listitem();
            const slot = $('<div class="slot"></div>');
            const icon = IconManager.getItemIcon({ itemType: category.type, itemNo: entry.itemNo, quantity: 1 });
            icon.on("mousemove", (event: any) => {
                TooltipManager.showTooltip(
                    TooltipManager.getItemTooltip({ itemType: category.type, itemNo: entry.itemNo }),
                    event.clientX,
                    event.clientY
                );
            });
            icon.on("mouseout", () => TooltipManager.hideTooltip());
            slot.append(icon);
            item._element.append(slot);
            category.list.append(item);
            item.on("clicked", this._onItemPicked.bind(this, category.type, entry.itemNo, entry.name));
        }
    }

    _onItemPicked(itemType: any, itemNo: any, name: any) {
        if (this._quantityDialog) {
            this._quantityDialog.close();
        }

        this._quantityDialog = ui.quantityDialog({ itemType, itemNo, name }, { min: 1, max: 999 });
        this._quantityDialog.on("confirm", (quantity: any) => {
            if (!SOCKETABLE_ITEM_TYPES.includes(itemType)) {
                netGame.chatSay(`/additem ${itemType} ${itemNo} ${quantity}`);
                return;
            }
            const socketPrompt = ui.messageBox(`Add a socket to ${name}?`, ["Yes", "No"]);
            socketPrompt.on("yes", () => netGame.chatSay(`/additem ${itemType} ${itemNo} ${quantity} 1`));
            socketPrompt.on("no", () => netGame.chatSay(`/additem ${itemType} ${itemNo} ${quantity}`));
        });
    }

    _renderSkillCategory(category: any, entries: any[]) {
        category.list.clear();

        for (const entry of entries) {
            const item = ui.listitem();
            const slot = $('<div class="slot"></div>');
            const icon = IconManager.getSkillIcon({ skillIdx: entry.skillIdx });
            icon.on("mousemove", (event: any) => {
                TooltipManager.showTooltip(
                    TooltipManager.getSkillTooltip({ skillIdx: entry.skillIdx }),
                    event.clientX,
                    event.clientY
                );
            });
            icon.on("mouseout", () => TooltipManager.hideTooltip());
            icon.attr("draggable", true);
            icon.on("dragstart", (event: any) => {
                event.originalEvent.dataTransfer.setData("text/plain", String(entry.skillIdx));
            });
            slot.append(icon);
            item._element.append(slot);
            category.list.append(item);
            item.on("doubleclicked", this._onSkillPicked.bind(this, entry.skillIdx));
        }
    }

    _onSkillPicked(skillIdx: any) {
        netGame.chatSay(`/learnskill ${skillIdx}`);
    }
}

ui.GamemasterDialog = GamemasterDialog;
ui.gamemasterDialog = function () {
    return new ui.GamemasterDialog();
};
